import { CommentAnchorType } from "../../../services/types";
import { CommentType } from "../../comments";

function isNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function cloneFlatComment(comment: CommentType): CommentType {
  return {
    ...comment,
    replies: [],
  };
}

function fallbackRootFromAnchor(anchor: CommentAnchorType): CommentType | null {
  if (!anchor.rootComment) return null;

  const rootComment = anchor.rootComment as unknown as CommentType;
  if (!isNumber(rootComment.id)) return null;

  return {
    ...rootComment,
    parentId: null,
    replies: [],
  };
}

/**
 * Returns a flat comment list containing the root anchored comment and all of
 * its descendants. CommentExplorer will rebuild the nested tree from this list.
 *
 * This keeps anchored discussions on the existing Comment model. The anchor is
 * only a locator; the discussion itself is still a normal root Comment thread.
 */
export function getAnchoredThreadComments(
  anchor: CommentAnchorType | null | undefined,
  comments: CommentType[]
): CommentType[] {
  if (!anchor || !isNumber(anchor.rootCommentId)) return [];

  const byId = new Map<number, CommentType>();
  const byParentId = new Map<number, CommentType[]>();

  (comments || []).forEach((comment) => {
    byId.set(comment.id, comment);

    if (isNumber(comment.parentId)) {
      const siblings = byParentId.get(comment.parentId) || [];
      siblings.push(comment);
      byParentId.set(comment.parentId, siblings);
    }
  });

  const result: CommentType[] = [];
  const seen = new Set<number>();

  const walk = (commentId: number) => {
    if (seen.has(commentId)) return;

    const comment = byId.get(commentId);
    if (!comment) return;

    seen.add(commentId);
    result.push(cloneFlatComment(comment));

    const children = byParentId.get(commentId) || [];
    children
      .slice()
      .sort((a, b) => {
        const byDate = Date.parse(a.createdAt || "") - Date.parse(b.createdAt || "");
        if (Number.isFinite(byDate) && byDate !== 0) return byDate;
        return a.id - b.id;
      })
      .forEach((child) => walk(child.id));
  };

  walk(anchor.rootCommentId);

  if (result.length > 0) return result;

  const fallbackRoot = fallbackRootFromAnchor(anchor);
  return fallbackRoot ? [fallbackRoot] : [];
}

export function getAnchoredThreadCommentCount(
  anchor: CommentAnchorType | null | undefined,
  comments: CommentType[]
): number {
  return getAnchoredThreadComments(anchor, comments).length;
}
